import React, { useEffect, useState } from "react";
import { publishShopStatus } from "@/lib/liveShopStatus";
import { vendorGetShop, vendorUpdateStatus, vendorShopVisibility } from "@/lib/vendorApi";
import { useApp } from "@/context/AppContext";
import { toast } from "sonner";
import AvailabilityToggle from "@/components/AvailabilityToggle";

/**
 * Shop Availability — reuses the exact visual pattern of the Product
 * Availability slider. Backend stays unchanged (/api/vendor/shops/{id}/status
 * with manual_open / manual_closed / auto).
 */
export default function ShopStatusCard({ shopId, onChanged }) {
  const { formatApiError } = useApp();
  const [shop, setShop] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!shopId) return;
    vendorGetShop(shopId).then(setShop).catch(() => {});
  };
  useEffect(load, [shopId]);

  if (!shop) return <div className="bg-white border border-slate-100 rounded-2xl p-5 h-32 animate-pulse" />;

  const mode = shop.status_mode === "manual_closed" ? "manual_closed" : "manual_open";
  const status = shop.status || { is_open: false, label: "Unknown" };
  const isOpen = !!status.is_open;

  const setStatus = async (newMode) => {
    setSaving(true);
    try {
      await vendorUpdateStatus(shopId, newMode);
      toast.success(newMode === "manual_open" ? "Shop is now OPEN" : "Shop is now CLOSED");
      publishShopStatus(shopId, newMode === "manual_open" ? { is_open: true, label: "Open", reason: "manual_open" } : { is_open: false, label: "Closed by shop", reason: "manual" });
      load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const onToggle = () => setStatus(isOpen ? "manual_closed" : "manual_open");
  const listed = shop.listed !== false;
  const toggleListed = async () => {
    if (listed && !window.confirm("Shop ko Inactive (hidden) karein? Shop, uske products aur services public search se chhup jayenge. Data, reviews aur ad history surakshit rahegi.")) return;
    setSaving(true);
    try {
      await vendorShopVisibility(shopId, !listed);
      toast.success(listed ? "Shop hidden from customers" : "Shop is visible again");
      load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-100 rounded-2xl p-4 sm:p-5" data-testid="shop-status-card">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] font-semibold text-slate-500 mb-1">Shop Status</div>
          <h3 className="font-display text-lg sm:text-xl font-bold">Real-time availability</h3>
        </div>
        <div className="text-xs text-slate-500">Customers see changes within seconds</div>
      </div>

      {/* The slider — identical UI/UX to Product Availability */}
      <AvailabilityToggle
        active={isOpen}
        busy={saving}
        onToggle={onToggle}
        eyebrow="Shop availability"
        activeTitle="Shop Open"
        inactiveTitle="Shop Closed"
        activeHint="customers see your shop as open"
        inactiveHint="customers see your shop as closed"
        ariaLabel="Toggle shop availability"
        testid="shop-status-toggle"
      />

      {/* Live status line */}
      <div className="mt-3 flex items-center justify-between text-xs text-slate-500 flex-wrap gap-2" data-testid="vendor-status-big">
        <div>
          Mode: <span className="font-semibold text-slate-700" data-testid="current-status-mode">
            {mode === "manual_open" ? "OPEN" : "CLOSED"}
          </span>
          <span className="mx-1.5 text-slate-300">·</span>
          <span className={isOpen ? "text-emerald-700 font-semibold" : "text-rose-700 font-semibold"}>{status.label}</span>
        </div>
      </div>

      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between gap-3 flex-wrap" data-testid="shop-visibility-row">
        <div className="text-xs text-slate-600">
          Listing: <span className={`ml-1 font-bold uppercase text-[10px] px-1.5 py-0.5 rounded-full ${listed ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`} data-testid="shop-visibility-status">{listed ? "Active" : "Inactive (hidden)"}</span>
          <div className="text-[11px] text-slate-400 mt-0.5">{listed ? "Customers can find your shop." : "Hidden from search — data is kept safe."}</div>
        </div>
        <button onClick={toggleListed} disabled={saving} className={`rounded-full px-4 py-2 text-xs font-semibold min-h-[40px] disabled:opacity-60 ${listed ? "border border-slate-200 text-slate-700 hover:border-amber-400 hover:text-amber-700" : "bg-emerald-700 hover:bg-emerald-800 text-white"}`} data-testid="shop-visibility-toggle">
          {listed ? "Deactivate shop" : "Activate shop"}
        </button>
      </div>
    </div>
  );
}
